/**
 * BusinessCanvasSkeleton
 *
 * A skeleton loading state for the BusinessCanvas component
 */

import React from 'react'
import { cn } from '@/lib/utils'

interface BusinessCanvasSkeletonProps {
  className?: string
}

export const BusinessCanvasSkeleton: React.FC<BusinessCanvasSkeletonProps> = ({ className }) => {
  return (
    <div
      className={cn(
        'border-border bg-card my-4 flex animate-pulse flex-col overflow-hidden rounded-lg border shadow-sm transition-all',
        className
      )}
    >
      {/* Header */}
      <div className="flex items-center border-b p-3">
        <div className="flex items-center gap-2">
          <div className="size-4 rounded bg-gray-200 dark:bg-gray-700"></div>
          <div className="h-4 w-32 rounded bg-gray-200 dark:bg-gray-700"></div>
        </div>
      </div>

      {/* Canvas grid */}
      <div className="p-4">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          {/* Left column */}
          <div className="space-y-4">
            {/* Key Partners */}
            <div className="rounded-md border p-3">
              <div className="mb-3 h-4 w-24 rounded bg-gray-200 dark:bg-gray-700"></div>
              <div className="space-y-2">
                <div className="h-3 w-full rounded bg-gray-200 dark:bg-gray-700"></div>
                <div className="h-3 w-5/6 rounded bg-gray-200 dark:bg-gray-700"></div>
              </div>
            </div>

            {/* Key Activities */}
            <div className="rounded-md border p-3">
              <div className="mb-3 h-4 w-24 rounded bg-gray-200 dark:bg-gray-700"></div>
              <div className="space-y-2">
                <div className="h-3 w-full rounded bg-gray-200 dark:bg-gray-700"></div>
                <div className="h-3 w-3/4 rounded bg-gray-200 dark:bg-gray-700"></div>
              </div>
            </div>

            {/* Key Resources */}
            <div className="rounded-md border p-3">
              <div className="mb-3 h-4 w-24 rounded bg-gray-200 dark:bg-gray-700"></div>
              <div className="space-y-2">
                <div className="h-3 w-5/6 rounded bg-gray-200 dark:bg-gray-700"></div>
                <div className="h-3 w-full rounded bg-gray-200 dark:bg-gray-700"></div>
              </div>
            </div>
          </div>

          {/* Middle column */}
          <div className="space-y-4">
            {/* Value Propositions */}
            <div className="h-full rounded-md border p-3">
              <div className="mb-3 h-4 w-32 rounded bg-gray-200 dark:bg-gray-700"></div>
              <div className="space-y-2">
                <div className="h-3 w-full rounded bg-gray-200 dark:bg-gray-700"></div>
                <div className="h-3 w-5/6 rounded bg-gray-200 dark:bg-gray-700"></div>
                <div className="h-3 w-4/5 rounded bg-gray-200 dark:bg-gray-700"></div>
                <div className="h-3 w-full rounded bg-gray-200 dark:bg-gray-700"></div>
              </div>
            </div>
          </div>

          {/* Right column */}
          <div className="space-y-4">
            {/* Customer Relationships */}
            <div className="rounded-md border p-3">
              <div className="mb-3 h-4 w-40 rounded bg-gray-200 dark:bg-gray-700"></div>
              <div className="space-y-2">
                <div className="h-3 w-full rounded bg-gray-200 dark:bg-gray-700"></div>
                <div className="h-3 w-3/4 rounded bg-gray-200 dark:bg-gray-700"></div>
              </div>
            </div>

            {/* Channels */}
            <div className="rounded-md border p-3">
              <div className="mb-3 h-4 w-20 rounded bg-gray-200 dark:bg-gray-700"></div>
              <div className="space-y-2">
                <div className="h-3 w-5/6 rounded bg-gray-200 dark:bg-gray-700"></div>
                <div className="h-3 w-full rounded bg-gray-200 dark:bg-gray-700"></div>
              </div>
            </div>

            {/* Customer Segments */}
            <div className="rounded-md border p-3">
              <div className="mb-3 h-4 w-32 rounded bg-gray-200 dark:bg-gray-700"></div>
              <div className="space-y-2">
                <div className="h-3 w-full rounded bg-gray-200 dark:bg-gray-700"></div>
                <div className="h-3 w-4/5 rounded bg-gray-200 dark:bg-gray-700"></div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom row */}
        <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
          {/* Cost Structure */}
          <div className="rounded-md border p-3">
            <div className="mb-3 h-4 w-28 rounded bg-gray-200 dark:bg-gray-700"></div>
            <div className="space-y-2">
              <div className="h-3 w-full rounded bg-gray-200 dark:bg-gray-700"></div>
              <div className="h-3 w-5/6 rounded bg-gray-200 dark:bg-gray-700"></div>
            </div>
          </div>

          {/* Revenue Streams */}
          <div className="rounded-md border p-3">
            <div className="mb-3 h-4 w-32 rounded bg-gray-200 dark:bg-gray-700"></div>
            <div className="space-y-2">
              <div className="h-3 w-full rounded bg-gray-200 dark:bg-gray-700"></div>
              <div className="h-3 w-4/5 rounded bg-gray-200 dark:bg-gray-700"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
